import { useEffect, useMemo, useState } from "react";
import {
  Percent, Save, Plus, Trash2, History, CheckCircle2, AlertTriangle,
  ArrowDownWideNarrow, Info, RotateCcw,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdminAPI } from "@/lib/api";
import { formatRupiah } from "@/lib/dummyData";
import { toast } from "sonner";

// 6 level organisasi (model bisnis final Bagian 5.1) — urut dari tertinggi.
const LEVELS = [
  { field: "pct_koperasi_pusat_shu", label: "Pusat",   admin: "Nasional",        def: 5.0 },
  { field: "pct_kantor_wilayah",     label: "Wilayah", admin: "Provinsi",        def: 12.0 },
  { field: "pct_kantor_daerah",      label: "Daerah",  admin: "Kabupaten/Kota",  def: 15.5 },
  { field: "pct_kantor_cabang",      label: "Cabang",  admin: "Kecamatan",       def: 17.5 },
  { field: "pct_kantor_ranting",     label: "Ranting", admin: "Desa/Kelurahan",  def: 20.0 },
  { field: "pct_unit_gerai_rtrw",    label: "Gerai",   admin: "Unit terkecil",   def: 30.0 },
];
const DEFAULT_FORM = Object.fromEntries(LEVELS.map((l) => [l.field, l.def]));

// Simulasi cascade (Bagian 4): level transaksi menyerap jatah level-level di bawahnya.
function simulateCascade(form, txIndex, amount) {
  return LEVELS.map((l, i) => {
    let pct = 0;
    if (i < txIndex) pct = Number(form[l.field]) || 0;
    else if (i === txIndex) pct = LEVELS.slice(i).reduce((s, x) => s + (Number(form[x.field]) || 0), 0);
    return { ...l, pct, amount: Math.round((pct / 100) * amount) };
  });
}

export default function RevenueSplit() {
  const [active, setActive] = useState(null);      // aturan aktif tersimpan
  const [form, setForm] = useState(DEFAULT_FORM);  // nilai yang sedang diedit
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const [applyRow, setApplyRow] = useState(null);  // versi riwayat yang akan diberlakukan lagi
  // Simulasi cascade
  const [simLevel, setSimLevel] = useState("3");   // index level transaksi (default Cabang)
  const [simAmount, setSimAmount] = useState(30000000);

  const load = () => Promise.all([
    AdminAPI.revenueSplit().then((s) => {
      setActive(s || null);
      if (s) setForm(Object.fromEntries(LEVELS.map((l) => [l.field, Number(s[l.field]) || 0])));
    }),
    AdminAPI.revenueSplitHistory().then((h) => setHistory(h || [])),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);

  const totalPct = useMemo(
    () => LEVELS.reduce((s, l) => s + (Number(form[l.field]) || 0), 0),
    [form]
  );
  const isValid = Math.abs(totalPct - 100) < 0.01;

  const setPct = (field, val) =>
    setForm((f) => ({ ...f, [field]: Math.max(0, Math.min(100, Number(val))) }));

  const payload = () => Object.fromEntries(LEVELS.map((l) => [l.field, Number(form[l.field]) || 0]));

  const save = async () => {
    if (!isValid) return toast.error(`Total harus 100% — sekarang ${totalPct.toFixed(2)}%`);
    setBusy(true);
    try {
      await AdminAPI.updateRevenueSplit(payload());
      toast.success("Persentase keuntungan wilayah disimpan & langsung berlaku");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menyimpan persentase");
    } finally { setBusy(false); }
  };

  const saveAsNew = async () => {
    if (!isValid) return toast.error(`Total harus 100% — sekarang ${totalPct.toFixed(2)}%`);
    setBusy(true);
    try {
      await AdminAPI.createRevenueSplit(payload());
      toast.success("Versi baru dibuat & diberlakukan (versi lama tersimpan di riwayat)");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal membuat versi baru");
    } finally { setBusy(false); }
  };

  const applyHistory = async () => {
    setBusy(true);
    try {
      await AdminAPI.createRevenueSplit(Object.fromEntries(LEVELS.map((l) => [l.field, Number(applyRow[l.field]) || 0])));
      toast.success("Versi riwayat diberlakukan kembali sebagai versi baru");
      setApplyRow(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal memberlakukan versi");
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await AdminAPI.deleteRevenueSplit(delRow.id);
      toast.success("Versi riwayat dihapus");
      setDelRow(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menghapus versi");
    } finally { setBusy(false); }
  };

  const txIndex = Number(simLevel);
  const simRows = useMemo(() => simulateCascade(form, txIndex, Number(simAmount) || 0), [form, txIndex, simAmount]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Persentase Keuntungan Wilayah</h1>
          <p className="text-ink/75 mt-2 text-sm max-w-2xl">
            Jatah keuntungan tiap tingkat organisasi (Pusat → Wilayah → Daerah → Cabang → Ranting → Gerai).
            Total keenam persentase wajib 100% setiap kali diubah.
          </p>
        </div>
        <Badge className="bg-forest/10 text-forest hover:bg-forest/10 rounded-full text-[10px] uppercase tracking-wider self-start lg:self-auto">
          <Percent size={11} className="mr-1" /> Model Bisnis 5.1
        </Badge>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Editor aturan aktif */}
        <Card className="lg:col-span-2 rounded-2xl border-stone-soft p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display font-semibold text-ink">Aturan yang Berlaku</h3>
              <p className="text-xs text-ink/55 mt-1">
                {active ? `Versi #${active.id} · diperbarui ${new Date(active.updated_at).toLocaleString("id-ID")}` : "Belum ada aturan tersimpan — simpan untuk membuat"}
              </p>
            </div>
            <Button
              variant="outline" size="sm" className="rounded-full border-stone-soft"
              onClick={() => setForm(DEFAULT_FORM)} data-testid="split-reset-default"
            >
              <RotateCcw size={13} /> Nilai Default Dokumen
            </Button>
          </div>

          <div className="space-y-3">
            {LEVELS.map((l, i) => (
              <div key={l.field} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-stone-soft p-3.5" data-testid={`split-row-${l.field}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-forest/10 text-forest grid place-items-center text-[11px] font-bold shrink-0">{i + 1}</span>
                    <span className="font-display font-semibold text-ink">{l.label}</span>
                    <span className="text-[11px] text-ink/50">· {l.admin}</span>
                  </div>
                  <Progress value={Math.min(100, Number(form[l.field]) || 0)} className="h-1.5 mt-2.5" />
                </div>
                <div className="relative">
                  <Input
                    type="number" min={0} max={100} step={0.5}
                    value={form[l.field]}
                    onChange={(e) => setPct(l.field, e.target.value)}
                    className="w-24 h-9 pr-7 text-right font-display font-bold text-ink rounded-lg"
                    data-testid={`split-input-${l.field}`}
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink/55 text-sm">%</span>
                </div>
              </div>
            ))}
          </div>

          {/* Validasi total + aksi simpan */}
          <div className={`mt-5 rounded-xl border-2 p-4 flex flex-col sm:flex-row items-center justify-between gap-3 ${isValid ? "border-forest bg-forest/5" : "border-terracotta bg-terracotta/5"}`} data-testid="split-total-validation">
            <div className="flex items-center gap-2 font-semibold" style={{ color: isValid ? "#2D5A27" : "#A94C38" }}>
              {isValid ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              Total: <span className="font-display text-xl">{totalPct.toFixed(1)}%</span>
              {!isValid && <span className="text-xs font-normal">(harus 100% — selisih {(100 - totalPct).toFixed(1)}%)</span>}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" className="rounded-full border-stone-soft"
                onClick={saveAsNew} disabled={!isValid || busy} data-testid="split-save-new"
              >
                <Plus size={14} /> Simpan sebagai Versi Baru
              </Button>
              <Button
                className="rounded-full bg-forest hover:bg-forest-dark text-white disabled:bg-stone-soft disabled:text-ink/40"
                onClick={save} disabled={!isValid || busy} data-testid="split-save"
              >
                <Save size={14} /> {busy ? "Menyimpan…" : "Simpan & Berlakukan"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Simulasi cascade */}
        <Card className="rounded-2xl border-stone-soft p-6">
          <div className="flex items-center gap-2">
            <ArrowDownWideNarrow size={16} className="text-forest" />
            <h3 className="font-display font-semibold text-ink">Simulasi Aturan Cascade</h3>
          </div>
          <p className="text-xs text-ink/55 mt-1 leading-relaxed">
            Bila transaksi terjadi di suatu level, level di bawahnya tidak menerima — jatahnya diserap level
            transaksi. Level di atasnya tetap dapat jatah normal.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider text-ink/55">Level Transaksi</Label>
              <Select value={simLevel} onValueChange={setSimLevel}>
                <SelectTrigger className="rounded-lg h-9" data-testid="split-sim-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l, i) => <SelectItem key={l.field} value={String(i)}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider text-ink/55">Keuntungan (Rp)</Label>
              <Input
                type="number" min={0} value={simAmount}
                onChange={(e) => setSimAmount(e.target.value)}
                className="rounded-lg h-9" data-testid="split-sim-amount"
              />
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            {simRows.map((r, i) => (
              <div key={r.field} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${i === txIndex ? "bg-forest/10 font-semibold text-forest" : r.pct === 0 ? "text-ink/35" : "text-ink/80 bg-cream/50"}`}>
                <span>{r.label}{i === txIndex && <span className="text-[10px] uppercase tracking-wider ml-1.5">· level transaksi</span>}</span>
                <span className="text-right">
                  <span className="font-display">{r.pct.toFixed(1)}%</span>
                  <span className="block text-[11px] font-normal">{formatRupiah(r.amount)}</span>
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 text-[11px] text-ink/55 bg-cream/60 rounded-lg p-2.5 mt-4">
            <Info size={13} className="shrink-0 mt-0.5" />
            Transaksi marketplace anggota tercatat di level Gerai (semua level dapat jatah normal); PO yang
            dinegosiasikan Cabang membuat Cabang menyerap jatah Ranting + Gerai.
          </div>
        </Card>
      </div>

      {/* Riwayat versi */}
      <Card className="rounded-2xl border-stone-soft p-6">
        <div className="flex items-center gap-2 mb-4">
          <History size={16} className="text-ink/55" />
          <h3 className="font-display font-semibold text-ink">Riwayat Versi Aturan</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="text-[10px] uppercase tracking-wider">Versi</TableHead>
                {LEVELS.map((l) => (
                  <TableHead key={l.field} className="text-[10px] uppercase tracking-wider text-right">{l.label}</TableHead>
                ))}
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Diperbarui</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((row) => (
                <TableRow key={row.id} className="border-stone-soft hover:bg-cream/30" data-testid={`split-history-${row.id}`}>
                  <TableCell className="font-semibold text-sm">#{row.id}</TableCell>
                  {LEVELS.map((l) => (
                    <TableCell key={l.field} className="text-right text-sm font-display">{Number(row[l.field]).toFixed(1)}%</TableCell>
                  ))}
                  <TableCell>
                    {row.is_active
                      ? <Badge className="bg-forest/10 text-forest hover:bg-forest/10 border-0 rounded-full text-[10px] uppercase tracking-wider">Berlaku</Badge>
                      : <Badge className="bg-stone-soft/40 text-ink/60 hover:bg-stone-soft/40 border-0 rounded-full text-[10px] uppercase tracking-wider">Riwayat</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-ink/70">{new Date(row.updated_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-right">
                    {!row.is_active && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="rounded-lg h-8 text-forest hover:bg-forest/10" onClick={() => setApplyRow(row)} data-testid={`split-apply-${row.id}`}>
                          Berlakukan
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-lg hover:bg-terracotta/10 text-ink/60 hover:text-terracotta h-8 w-8" onClick={() => setDelRow(row)} data-testid={`split-delete-${row.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {history.length === 0 && (
            <p className="py-8 text-center text-sm text-ink/55">Belum ada riwayat versi</p>
          )}
        </div>
      </Card>

      {/* Konfirmasi berlakukan versi riwayat */}
      <AlertDialog open={!!applyRow} onOpenChange={(v) => !v && setApplyRow(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Berlakukan versi #{applyRow?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              Nilai versi ini akan disalin menjadi versi baru yang langsung berlaku. Aturan yang sekarang berlaku
              tetap tersimpan di riwayat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-forest hover:bg-forest-dark" onClick={applyHistory} disabled={busy} data-testid="split-apply-confirm">
              {busy ? "Memproses…" : "Ya, Berlakukan"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Konfirmasi hapus versi riwayat */}
      <AlertDialog open={!!delRow} onOpenChange={(v) => !v && setDelRow(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Hapus versi #{delRow?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              Versi riwayat ini akan dihapus permanen. Aturan yang sedang berlaku tidak dapat dihapus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-terracotta hover:bg-terracotta/90" onClick={confirmDelete} disabled={busy} data-testid="split-delete-confirm">
              {busy ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
